import React, { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_API_URL}`;

function MobileProviderHome({ user, onSearch, onViewAll }) {
  const [stats, setStats] = useState({ total: 0, pending: 0, accepted: 0, rejected: 0, revoked: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/Provider/my-uploads/${user?.id}`)
      .then((r) => r.json())
      .then((data) => {
        setRecent(data.slice(0, 5));

        // ✅ Unique accepted patients (not revoked)
        const uniqueAccepted = new Set(
          data.filter((d) => d.status === 'accepted').map((d) => d.patientUid)
        ).size;

        // ✅ Unique revoked patients
        const uniqueRevoked = new Set(
          data.filter((d) => d.status === 'revoked').map((d) => d.patientUid)
        ).size;

        setStats({
          total: data.length,
          pending: data.filter((d) => d.status === 'pending').length,
          accepted: uniqueAccepted,
          rejected: data.filter((d) => d.status === 'rejected').length,
          revoked: uniqueRevoked,
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getReportIcon = (type) => {
    if (!type) return 'description';
    const t = type.toLowerCase();
    if (t.includes('blood')) return 'bloodtype';
    if (t.includes('ecg') || t.includes('heart')) return 'monitor_heart';
    if (t.includes('mri') || t.includes('scan')) return 'radiology';
    if (t.includes('urine')) return 'water_drop';
    return 'biotech';
  };

  const statCards = [
    { label: 'TOTAL UPLOADED', value: stats.total, color: '#2563eb', iconBg: '#eff6ff', borderColor: '#2563eb', icon: 'note_add' },
    { label: 'ACCEPTED', value: stats.accepted, color: '#059669', iconBg: '#ecfdf5', borderColor: '#10b981', icon: 'check_circle' },
    { label: 'PENDING', value: stats.pending, color: '#d97706', iconBg: '#fffbeb', borderColor: '#f59e0b', icon: 'schedule' },
    { label: 'REJECTED', value: stats.rejected, color: '#dc2626', iconBg: '#fef2f2', borderColor: '#ef4444', icon: 'cancel' },
    { label: 'REVOKED', value: stats.revoked, color: '#7c3aed', iconBg: '#f5f3ff', borderColor: '#7c3aed', icon: 'block' },
  ];

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", padding: '16px 16px 24px 16px', display: 'flex', flexDirection: 'column', gap: '16px', backgroundColor: '#f8fafc' }}>

      {/* Hero Banner */}
      <section className="relative w-full rounded-3xl p-6 overflow-hidden flex flex-col justify-center shadow-lg text-white"
        style={{ background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 50%, #7c3aed 100%)', minHeight: '120px' }}>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-20 pointer-events-none select-none">
          <span className="material-symbols-outlined" style={{ fontSize: '110px', transform: 'rotate(-10deg)' }}>biotech</span>
        </div>
        <div className="relative z-10">
          <p className="text-white/80 text-xs font-medium mb-1 flex items-center gap-1.5">Good Morning, <span className="text-sm">👋</span></p>
          <h2 className="text-white font-extrabold text-2xl tracking-tight">{user?.name || 'Apollo Diagnostics'}</h2>
        </div>
      </section>

      {/* Stats Grid — 2 cols, last card full width if odd */}
      <section className="grid grid-cols-2 gap-3">
        {statCards.map((s, idx) => (
          <div key={s.label}
            className={`bg-white rounded-2xl p-4 flex items-center gap-3 relative overflow-hidden ${idx === 4 ? 'col-span-2' : ''}`}
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.03)', borderLeft: `4px solid ${s.borderColor}` }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: s.iconBg, color: s.color }}>
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>{s.icon}</span>
            </div>
            <div>
              <p className="text-[9px] font-extrabold text-gray-400 tracking-wider mb-0.5 uppercase">{s.label}</p>
              <p className="text-2xl font-black" style={{ color: s.color }}>{s.value}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Action Button */}
      <button onClick={onSearch} className="w-full rounded-2xl text-white font-bold flex items-center justify-center gap-3 transition-all active:scale-[0.98] shadow-md"
        style={{ background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)', padding: '16px' }}>
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
          <span className="material-symbols-outlined text-white" style={{ fontSize: '18px' }}>search</span>
        </div>
        <span className="text-sm font-bold">Search Patient and Upload Report</span>
      </button>

      {/* Recent Uploads */}
      <section className="mt-1">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-base text-gray-900">Recent Uploads</h3>
          <button onClick={onViewAll} className="text-xs font-bold text-blue-600 flex items-center gap-0.5 hover:underline">
            VIEW ALL <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>chevron_right</span>
          </button>
        </div>

        {recent.length === 0 ? (
          <div className="bg-white rounded-2xl p-6 shadow-sm flex flex-col items-center justify-center text-center border border-slate-100">
            <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center mb-2">
              <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>folder_open</span>
            </div>
            <p className="font-bold text-sm text-slate-800">No uploads yet</p>
            <p className="text-xs text-slate-400 mt-0.5">Reports you upload will appear here</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {recent.map((u) => (
              <div key={u.id} className="bg-white rounded-2xl p-3 shadow-sm flex items-center justify-between border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    u.status === 'accepted' ? 'bg-emerald-50 text-emerald-600' :
                    u.status === 'rejected' ? 'bg-rose-50 text-rose-600' :
                    u.status === 'revoked' ? 'bg-purple-50 text-purple-600' :
                    'bg-amber-50 text-amber-600'
                  }`}>
                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>{getReportIcon(u.reportType)}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-gray-900 truncate max-w-[160px]">{u.fileName}</p>
                    <p className="text-xs text-gray-400">{new Date(u.uploadedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                </div>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase shrink-0 ${
                  u.status === 'accepted' ? 'bg-emerald-50 text-emerald-700' :
                  u.status === 'rejected' ? 'bg-rose-50 text-rose-700' :
                  u.status === 'revoked' ? 'bg-purple-50 text-purple-700' :
                  'bg-amber-50 text-amber-700'
                }`}>
                  {u.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default MobileProviderHome;